import { useCallback } from 'react';
import { AsyncPaginate, LoadOptions } from 'react-select-async-paginate';
import { GroupBase } from 'react-select';

interface AsyncAutocompleteProps<T> {
  value: T | null;
  onChange: (value: T | null) => void;
  fetchItems: (query: string, page: number) => Promise<{
    items: T[];
    hasMore: boolean;
  }>;
  getLabel: (item: T) => string;
  placeholder?: string;
  menuPortal?: boolean;
  /** Debounce timeout in milliseconds (default: 300ms) */
  debounceTimeout?: number;
}

const defaultAdditional = { page: 1 };

function AsyncAutocomplete<T>({
  value,
  onChange,
  fetchItems,
  getLabel,
  placeholder = 'Search...',
  menuPortal = false,
  debounceTimeout = 300,
}: AsyncAutocompleteProps<T>) {
  const loadOptions: LoadOptions<T, GroupBase<T>, { page: number }> = useCallback(
    async (search, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;
      const result = await fetchItems(search, page);

      return {
        options: result.items,
        hasMore: result.hasMore,
        additional: { page: page + 1 },
      };
    },
    [fetchItems]
  );

  return (
    <AsyncPaginate
      placeholder={placeholder}
      getOptionLabel={getLabel}
      additional={defaultAdditional}
      value={value}
      onChange={onChange}
      loadOptions={loadOptions}
      debounceTimeout={debounceTimeout}
      {...(menuPortal && {
        menuPortalTarget: document.body,
        menuPosition: 'fixed' as const,
        styles: {
          menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
        },
      })}
    />
  );
}

export default AsyncAutocomplete;
